/**
 * Currency Amount Value Object
 * 
 * Represents monetary amounts with both base and display currency values.
 * All amounts are stored in cents (integers) for precision.
 */

export interface CurrencyAmount {
  base: number;    // Amount in cents (base/accounting currency)
  display: number; // Amount in cents (display/presentment currency)
}

/**
 * Create CurrencyAmount from base amount (assumes same for display initially)
 */
export function createCurrencyAmount(baseCents: number, displayCents?: number): CurrencyAmount {
  return {
    base: baseCents,
    display: displayCents ?? baseCents,
  };
}

/** Exchange rate from base (USD) to display currency for cart payloads. */
export function currencyRatioFromAmounts(baseCents: number, displayCents: number): number {
  if (displayCents <= 0) return 1;
  return baseCents / displayCents;
}

/**
 * Create CurrencyAmount from decimal strings (from API)
 */
export function createCurrencyAmountFromStrings(
  baseString: string,
  displayString?: string
): CurrencyAmount {
  const parsedBase = Number.parseFloat(baseString);
  const parsedDisplay = displayString ? Number.parseFloat(displayString) : parsedBase;

  if (!Number.isFinite(parsedBase) || !Number.isFinite(parsedDisplay)) {
    throw new Error('Invalid currency amount');
  }

  const baseCents = Math.round(parsedBase * 100);
  const displayCents = Math.round(parsedDisplay * 100);
  
  return {
    base: baseCents,
    display: displayCents,
  };
}

/**
 * Convert CurrencyAmount to decimal number (for display)
 */
export function currencyAmountToDecimal(amount: CurrencyAmount, useDisplay: boolean = true): number {
  const cents = useDisplay ? amount.display : amount.base;
  return cents / 100;
}

/**
 * Format CurrencyAmount as currency string.
 *
 * @param symbolOverride - Pass `countryConfig.currency.format` (e.g. "CA$", "AU$")
 *   to replace the Intl-derived symbol and guarantee the correct display symbol.
 */
export function formatCurrencyAmount(
  amount: CurrencyAmount,
  currency: string,
  locale: string = 'en-US',
  useDisplay: boolean = true,
  symbolOverride?: string
): string {
  const decimal = currencyAmountToDecimal(amount, useDisplay);
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });

  if (!symbolOverride) {
    return formatter.format(decimal);
  }

  return formatter
    .formatToParts(decimal)
    .map((part) => (part.type === 'currency' ? symbolOverride : part.value))
    .join('');
}
